import * as React from 'react';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import { ProductPricing } from '@services/merch/types/api/create-product';
import AppState from '@webapp/store/state';
import { Merch } from '@webapp/store/merch/state';
import { connect } from 'react-redux';
import { UpdateMerchProductError } from '@webapp/store/merch/action-creators';
import { Dispatch } from 'redux';
import Grid from '@components/basics/grid';

export type MerchProductDetailData = {
  productName: string;
  productPrice: string;
};

const cleanPrice = (price: string) => price.replace(/[^\d.]/g, '');

interface MerchProductDetailProps {
  className?: string;
  pricingData?: ProductPricing;
  onChange: (data: MerchProductDetailData) => void;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateMerchProductError;
}

interface MerchProductStateProps {
  active?: Partial<Merch>;
}

const mapStateToProps = (state: AppState) => ({
  active: state.merch.active,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  sendAnalytics: (field: string, value: string, error: string) =>
    dispatch(new UpdateMerchProductError(error, field, value)),
});

export const MerchProductDetail: React.FC<
  MerchProductDetailProps & MerchProductStateProps
> = ({ className, active, pricingData, onChange, sendAnalytics }) => {
  const [productName, setProductName] = React.useState<string>('');

  const [productPrice, setProductPrice] = React.useState<string | undefined>(
    undefined
  );

  const [priceError, setPriceError] = React.useState('');
  const [nameError, setNameError] = React.useState('');

  const productProfit = React.useMemo(() => {
    const cleanProductPrice = cleanPrice(productPrice || '0');
    if (pricingData && cleanProductPrice) {
      const result =
        parseFloat(cleanProductPrice) - pricingData.production_cost;

      if (Number.isNaN(result) || result < 0) {
        return '';
      }

      return result.toFixed(2);
    }

    return '';
  }, [pricingData, productPrice]);

  const productProfitMargin = React.useMemo(() => {
    if (pricingData) {
      if (productPrice) {
        const cleanProductPrice = cleanPrice(productPrice || '0');
        const result =
          (100 * (parseFloat(cleanProductPrice) - pricingData.production_cost)) /
          pricingData.production_cost;

        if (Number.isNaN(result) || result < 0) {
          return '';
        }

        return result.toFixed(0);
      }

      return pricingData.profit_suggested_percentage * 50;
    }

    return '';
  }, [pricingData, productPrice]);

  const handlePriceChange = (value: string) => {
    setPriceError('');
    setProductPrice(value);

    const cleanValue = cleanPrice(value);

    onChange({
      productName,
      productPrice: cleanValue,
    });

    if (cleanValue && pricingData) {
      if (
        !Number(cleanValue) ||
        Number(cleanValue) < pricingData?.production_cost
      ) {
        setPriceError('Invalid price.');
        sendAnalytics('Merch price', cleanValue, 'INVALID.MERCH.PRICE');
      }
    } else {
      setPriceError('The price is required.');
      sendAnalytics('Merch price', cleanValue, 'MISSING.MERCH.PRICE');
    }
  };

  const handleNameChange = (value: string) => {
    setProductName(value);
    setNameError('');

    onChange({
      productName: value,
      productPrice:
        productPrice ??
        `${Math.ceil(((pricingData?.production_cost || 0) * 2) / 10) * 10}`,
    });

    if (!value.length) {
      setNameError('The name is required.');
      sendAnalytics('Merch name', value, 'MISSING.MERCH.NAME');
    }
  };

  React.useEffect(() => {
    getNameWithReleaseTitle(
      active?.editProduct?.title || active?.product?.title || '',
      active?.associatedRelease?.title || ''
    );
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (pricingData && productPrice === undefined) {
      if (pricingData.suggested_price) {
        setProductPrice(`${pricingData.suggested_price}`);
      } else if (pricingData.production_cost) {
        setProductPrice(
          `${(
            Math.ceil(((pricingData.production_cost || 0) * 1.5) / 10) * 10
          ).toFixed(0)}`
        );
      }
    }
  }, [pricingData, productPrice]);

  const getNameWithReleaseTitle = (
    productTitle: string,
    releaseTitle: string
  ) => {
    let productName = productTitle;
    if (releaseTitle) {
      productName = releaseTitle + ' ' + productTitle;
    }
    setProductName(productName);
  };
  const parsePrice = (price?: string): string => {
    if (!price) {
      return '';
    }

    return `$${cleanPrice(price)}`;
  };

  return (
    <MotionOpacity className={className}>
      <Typography className="mb-20" customFont="medium" type="big-title">
        <Translate id="MERCH.SECTIONS.PRICING.DETAILS" />
      </Typography>
      <Grid gap={10}>
        <InputField
          label={<Translate id="MERCH.SECTIONS.PRICING.DETAILS.PRODUCT.NAME" />}
          value={productName}
          error={nameError}
          onChange={handleNameChange}
        />
        <Grid gap={5}>
          <InputField
            label={
              <Translate id="MERCH.SECTIONS.PRICING.DETAILS.PRODUCT.PRICE" />
            }
            value={parsePrice(productPrice)}
            error={priceError}
            onChange={handlePriceChange}
          />
          <CostInputWrapper gap={5}>
            <InputField
              className="half"
              label={
                <Translate id="MERCH.SECTIONS.PRICING.DETAILS.PRODUCT.COST" />
              }
              value={`$${pricingData?.production_cost}`}
              locked
            />
            <InputField
              className="half"
              label={
                <Translate id="MERCH.SECTIONS.PRICING.DETAILS.PRODUCT.MARGIN" />
              }
              value={`$${productProfit} (${productProfitMargin}%)`}
              locked
            />
          </CostInputWrapper>
        </Grid>
      </Grid>
    </MotionOpacity>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MerchProductDetail);

const CostInputWrapper = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  flex-direction: column;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    flex-direction: row;
  }

  .half {
    flex-grow: 1;
    width: 100%;
  }
`;
